import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Loader2 } from 'lucide-react';
import { useE2E } from '../context/E2EContext';
import { toast } from 'sonner';

/**
 * Prompts the current user for their password to unlock the E2E private key
 * after a page reload / new tab / cleared sessionStorage.
 *
 * Rendered globally in App.js; auto-shows when `e2e.needsUnlock === true`.
 */
export default function E2EUnlockModal() {
  const e2e = useE2E();
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!e2e?.needsUnlock || e2e.ready) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    const res = await e2e.unlock(password, remember);
    setBusy(false);
    if (res.ok) {
      toast.success(remember ? 'Crittografia sbloccata per 7 giorni' : 'Crittografia sbloccata');
      setPassword('');
    } else if (res.reason === 'wrong_password') {
      toast.error('Password errata');
    } else {
      toast.error('Sblocco fallito, riprova');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="e2e-unlock-modal"
    >
      <motion.form
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl bg-[var(--bg-void)] border border-[var(--gold)]/30 p-6 space-y-4"
      >
        <div className="flex flex-col items-center text-center gap-2">
          <div className="w-12 h-12 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/30 flex items-center justify-center">
            <Lock className="w-5 h-5 text-[var(--gold)]" />
          </div>
          <div className="font-heading text-lg">Sblocca la crittografia</div>
          <div className="text-xs text-white/60 leading-relaxed">
            Per motivi di sicurezza, la chiave delle chat cifrate è stata rimossa dal tuo dispositivo.<br/>
            Inserisci la tua password per continuare a leggere e inviare messaggi cifrati.
          </div>
        </div>

        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="La tua password"
          data-testid="e2e-unlock-password"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--gold)]/50"
          autoFocus
        />

        <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="e2e-unlock-remember-label">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            data-testid="e2e-unlock-remember"
            className="mt-0.5 w-4 h-4 accent-[var(--gold)] cursor-pointer"
          />
          <span className="text-xs text-white/80 leading-snug">
            Ricorda per 7 giorni su questo dispositivo
            <span className="block text-[10px] text-white/40 mt-0.5">
              Non chiederà più la password su questo browser fino alla scadenza. Non attivare su dispositivi condivisi.
            </span>
          </span>
        </label>

        <button
          type="submit"
          disabled={!password || busy}
          data-testid="e2e-unlock-submit"
          className="w-full h-11 rounded-full bg-[var(--gold)] text-black font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          Sblocca
        </button>

        <div className="text-[10px] text-white/40 text-center">
          Non salviamo la password. Viene usata solo per decifrare la chiave e poi scartata.
        </div>
      </motion.form>
    </motion.div>
  );
}
